import React from 'react'
import styled from 'styled-components'

import {
  CardTitle,
  SectionCard,
} from 'features/trend-detection/components/insight-detail-styles'
import {
  EMERGENCE_COLORS,
  EMERGENCE_NOTHING_COLOR,
} from 'features/trend-detection/components/issue-constants'
import { EmergenceBarChart } from 'features/trend-detection/components/IssueShared'
import type { EmergenceLevel } from 'features/trend-detection/utils/helpers'

export type SinceDetectionDirection =
  | 'increased'
  | 'decreased'
  | 'remains'
  | 'returned_to_baseline'
  | 're_emerged'

export interface ChangesSinceDetectionFields {
  /** Bucket label at initial detection. Null on re-emergence (no prior baseline). */
  fromLevel: EmergenceLevel | null
  /** Bucket label for the current update. */
  toLevel: EmergenceLevel
  /** Which Bullet 1 verb to render. */
  directionSinceDetection: SinceDetectionDirection
  /** Signed % above baseline. Null when impact_rate is undefined (re-emergence). */
  pctAboveBaseline: number | null
  /** Bullet 2 deadband state — share is within ±5% of baseline or below. */
  isBackToBaseline: boolean
}

export interface ChangesSinceDetectionProps {
  /** Backend-computed comparison fields. The component is a pure renderer. */
  changes: ChangesSinceDetectionFields | null | undefined
}

const formatLevelLabel = (level: EmergenceLevel): string =>
  level.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())

const levelColor = (level: EmergenceLevel): string =>
  level === 'nothing' ? EMERGENCE_NOTHING_COLOR : EMERGENCE_COLORS[level]

const LevelChip: React.FC<{ level: EmergenceLevel }> = ({ level }) => (
  <LevelChipWrap>
    <EmergenceBarChart level={level} />
    <LevelLabel $color={levelColor(level)}>
      {formatLevelLabel(level)}
    </LevelLabel>
  </LevelChipWrap>
)

export const ChangesSinceDetection: React.FC<ChangesSinceDetectionProps> = ({
  changes,
}) => {
  if (!changes) return null

  return (
    <SectionCard>
      <CardTitle>Changes since detection</CardTitle>
      <BulletList>
        <BulletRow>
          <Bullet>•</Bullet>
          <BulletText>{renderLevelBullet(changes)}</BulletText>
        </BulletRow>
        <BulletRow>
          <Bullet>•</Bullet>
          <BulletText>{renderShareBullet(changes)}</BulletText>
        </BulletRow>
      </BulletList>
    </SectionCard>
  )
}

const renderLevelBullet = ({
  fromLevel,
  toLevel,
  directionSinceDetection,
}: ChangesSinceDetectionFields): React.ReactNode => {
  switch (directionSinceDetection) {
    case 're_emerged':
      return (
        <>
          Elevation level <Bold>re-emerged</Bold> at{' '}
          <LevelChip level={toLevel} />
        </>
      )
    case 'returned_to_baseline':
      return (
        <>
          Elevation level <Bold>returned to baseline</Bold>
        </>
      )
    case 'remains':
      return (
        <>
          Elevation level <Bold>remains</Bold> <LevelChip level={toLevel} />
        </>
      )
    case 'increased':
    case 'decreased':
      return (
        <>
          Elevation level <Bold>{directionSinceDetection}</Bold> from{' '}
          {fromLevel && <LevelChip level={fromLevel} />} to{' '}
          <LevelChip level={toLevel} />
        </>
      )
  }
}

const renderShareBullet = ({
  pctAboveBaseline,
  isBackToBaseline,
}: ChangesSinceDetectionFields): React.ReactNode => {
  if (isBackToBaseline) {
    return (
      <>
        Share of mentions is <Bold>back to baseline</Bold>
      </>
    )
  }
  if (pctAboveBaseline === null) {
    return (
      <>
        Share of mentions — <Bold>newly emerging</Bold>
      </>
    )
  }
  return (
    <>
      Share of mentions is{' '}
      <Bold>{Math.abs(pctAboveBaseline)}% above baseline</Bold>
    </>
  )
}

const BulletList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const BulletRow = styled.li`
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #6c737d;
`

const Bullet = styled.span`
  flex-shrink: 0;
  line-height: 22px;
`

const BulletText = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  row-gap: 4px;
`

const LevelChipWrap = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`

const LevelLabel = styled.span<{ $color: string }>`
  font-weight: 600;
  color: ${(p) => p.$color};
`

const Bold = styled.span`
  font-weight: 600;
`
